<!--
  AI:
  Status: "confirmed"
  Type: Component
  SubType: CrudIntegration
  Reason: The ONE reusable SEPA mandate block of cr.CustomerBankAccount (projects/sepa_storage
    R-32/R-33): status badge, mandate source picker and the three capture branches
    (withoutDocument / withoutDocumentNoReason / withDocument / withSmProcess). Carries NO account data — IBAN, BIC and
    account holder belong to the embedding OBCs.
-->
@if (initialized) {

    <div class="cr-sepa-mandate-capture flex flex-column gap-3">

        <!-- Status header -->
        <div class="flex align-items-center justify-content-between gap-2 flex-wrap">

            <div class="flex align-items-center gap-2">
                <i class="fa-regular fa-file-signature text-color-secondary"></i>
                <span class="font-medium">{{ getHeaderLabel() }}</span>
            </div>

            <div class="flex align-items-center gap-2">
                @if (busy) {
                    <p-progressSpinner strokeWidth="4" styleClass="w-1rem h-1rem"></p-progressSpinner>
                }
                <p-tag
                        [severity]="getStatusBadge().severity"
                        [icon]="getStatusBadge().icon"
                        [value]="getStatusBadge().label">
                </p-tag>
            </div>
        </div>

        <!-- Failed backend run: without the message a failed mandate run is not diagnosable (D-22) -->
        @if (state?.errorFlag) {
            <p-message severity="error" styleClass="w-full">
                <div class="flex flex-column gap-1">
                    <span class="font-medium">Die Mandatsverarbeitung ist fehlgeschlagen.</span>
                    @if (state.errorMessage) {
                        <span class="text-sm">{{ state.errorMessage }}</span>
                    } @else {
                        <span class="text-sm">Es wurde kein Fehlertext übermittelt.</span>
                    }
                </div>
            </p-message>
        }

        <!--
          Neutral progress note. Carries what mandateCaptured no longer says: the action ran, but
          the mandate is not valid yet (or failed without a message of its own).
        -->
        @if (getProgressMessage(); as progressMessage) {
            <p-message [severity]="progressMessage.severity" styleClass="w-full">
                <span class="text-sm">{{ progressMessage.text }}</span>
            </p-message>
        }

        <!-- Failed status read — the badge above may be stale, so say so rather than imply "none" -->
        @if (stateErrorMessage) {
            <p-message severity="error" styleClass="w-full">
                <span class="text-sm">{{ stateErrorMessage }}</span>
            </p-message>
        }

        <!--
          Failed account read — the mandate source persisted on the account is unknown, so the
          picker below comes up empty and a fresh pick would overwrite a source nobody saw.
        -->
        @if (accountErrorMessage) {
            <p-message severity="error" styleClass="w-full">
                <div class="flex flex-column gap-2">
                    <span class="text-sm">{{ accountErrorMessage }}</span>
                    <div>
                        <p-button
                                label="Erneut laden"
                                icon="fa-regular fa-rotate-right"
                                severity="secondary"
                                [outlined]="true"
                                [disabled]="busy"
                                (onClick)="handleReloadSources()">
                        </p-button>
                    </div>
                </div>
            </p-message>
        }

        <!-- Failure of an action or a read triggered here -->
        @if (actionErrorMessage) {
            <p-message severity="error" styleClass="w-full">
                <span class="text-sm">{{ actionErrorMessage }}</span>
            </p-message>
        }

        <!-- Mandate summary, as soon as anything was captured -->
        @if (state && state.sepaMandateStatus !== CustomerSepaMandateStatusEnum.none) {
            <div class="flex flex-column gap-2 surface-50 border-round p-3">

                @if (state.sepaMandateIdentifier) {
                    <div class="flex gap-2 flex-wrap">
                        <span class="text-sm text-color-secondary w-12rem">Mandatsreferenz</span>
                        <span class="text-sm font-medium">{{ state.sepaMandateIdentifier }}</span>
                    </div>
                }

                @if (state.sepaDateOfSignature) {
                    <div class="flex gap-2 flex-wrap">
                        <span class="text-sm text-color-secondary w-12rem">Unterschriftsdatum</span>
                        <span class="text-sm">{{ state.sepaDateOfSignature | date: 'dd.MM.yyyy' }}</span>
                    </div>
                }

                @if (state.sepaMandateRevokedDate) {
                    <div class="flex gap-2 flex-wrap">
                        <span class="text-sm text-color-secondary w-12rem">Widerrufen am</span>
                        <span class="text-sm">{{ state.sepaMandateRevokedDate | date: 'dd.MM.yyyy' }}</span>
                    </div>
                }

                @if (state.sepaMandateDocumentDtoId) {
                    <div class="flex gap-2 align-items-center flex-wrap">
                        <span class="text-sm text-color-secondary w-12rem">Mandatsdokument</span>
                        <p-button
                                label="Dokument anzeigen"
                                icon="fa-regular fa-file-lines"
                                severity="secondary"
                                [text]="true"
                                (onClick)="handleOpenDocument()">
                        </p-button>
                    </div>
                }

                @if (isRevocationAllowed()) {
                    <div class="flex justify-content-end">
                        <p-button
                                label="Mandat widerrufen"
                                icon="fa-regular fa-ban"
                                severity="danger"
                                [outlined]="true"
                                [disabled]="busy"
                                (onClick)="handleRevoke()">
                        </p-button>
                    </div>
                }
            </div>
        }

        <!-- Create context before the account was saved -->
        @if (isWaitingForBankAccount()) {
            <p-message severity="info" styleClass="w-full">
                <span class="text-sm">
                    Bitte speichern Sie zuerst die Bankverbindung. Das Mandat braucht die
                    gespeicherte Bankverbindung als Bezug.
                </span>
            </p-message>
        }

        <!-- Capture area -->
        @if (isCaptureVisible()) {

            <div class="flex flex-column gap-3">

                <!-- 1. Source selection -->
                <div class="flex flex-column gap-2">

                    <!--
                      `for` only while the picker is really mounted - the read-only branch has no
                      control carrying that id, and a `for` pointing at nothing is worse for a screen
                      reader than no `for` at all.
                    -->
                    <label
                            [attr.for]="isSourcePickerVisible() ? 'crSepaMandateSource' : null"
                            class="font-medium">Mandatsquelle</label>

                    <!--
                      No showClear on purpose: clearing would only empty the picker, while the
                      account keeps the source it was given. Removing a source is not a capture
                      step - the way back out of a mandate is the revocation, not an empty field.
                    -->
                    @if (sourcesErrorMessage) {
                        <p-message severity="error" styleClass="w-full">
                            <div class="flex flex-column gap-2">
                                <span class="text-sm">{{ sourcesErrorMessage }}</span>
                                <div>
                                    <p-button
                                            label="Erneut laden"
                                            icon="fa-regular fa-rotate-right"
                                            severity="secondary"
                                            [outlined]="true"
                                            [disabled]="busy"
                                            (onClick)="handleReloadSources()">
                                    </p-button>
                                </div>
                            </div>
                        </p-message>
                    } @else if (!isSourcePickerVisible()) {
                        <!--
                          The source was already chosen while the account was created and is stored
                          on the row: a read-only line instead of the picker. Branching out HERE,
                          ahead of the two picker branches, keeps the "sources could not be loaded"
                          message at exactly one place - without that ordering this line would claim
                          "keine Mandatsquelle hinterlegt" while the account in fact has one and only
                          the LIST is missing.
                        -->
                        <div class="flex align-items-center gap-2 flex-wrap">
                            <span class="text-sm">{{ getSelectedSourceDisplayLabel() }}</span>
                            <p-button
                                    label="Ändern"
                                    icon="fa-regular fa-pen"
                                    severity="secondary"
                                    [link]="true"
                                    [disabled]="busy"
                                    (onClick)="handleRevealSourcePicker()">
                            </p-button>
                        </div>
                    } @else if (sourceOptions.length === 0) {
                        <p-message severity="warn" styleClass="w-full">
                            <span class="text-sm">
                                Es ist keine Mandatsquelle konfiguriert. Ohne Quelle kann kein
                                Mandat erfasst werden.
                            </span>
                        </p-message>
                    } @else {
                        <p-select
                                inputId="crSepaMandateSource"
                                [options]="sourceOptions"
                                [(ngModel)]="selectedSourceId"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Mandatsquelle wählen"
                                [disabled]="busy"
                                appendTo="body"
                                styleClass="w-full"
                                (onChange)="handleSourceChange()">

                            <ng-template let-option pTemplate="item">
                                <div class="flex align-items-center justify-content-between gap-2 w-full">
                                    <span>{{ option.label }}</span>
                                    <p-tag
                                            [severity]="getInternalTypeBadge(option.internalType).severity"
                                            [icon]="getInternalTypeBadge(option.internalType).icon"
                                            [value]="getInternalTypeBadge(option.internalType).label">
                                    </p-tag>
                                </div>
                            </ng-template>
                        </p-select>
                    }

                    @if (selectedSource) {
                        <div class="flex align-items-center gap-2">
                            <span class="text-sm text-color-secondary">Erfassungsart</span>
                            <p-tag
                                    [severity]="getInternalTypeBadge(selectedSource.internalType).severity"
                                    [icon]="getInternalTypeBadge(selectedSource.internalType).icon"
                                    [value]="getInternalTypeBadge(selectedSource.internalType).label">
                            </p-tag>
                        </div>
                    }
                </div>

                <!-- 2. Branch by internalType (R-33) -->
                @if (selectedSource) {

                    <!--
                      2a. withoutDocument: signature date + mandatory reason, captured through the
                      capture ACTION. The embedded mvs-crud-object is the metadata-driven INPUT
                      SURFACE only and never saves - a CRUD write of these two attributes cannot
                      activate a mandate (CustomerBankAccountOnChange derives a status, it never
                      raises one), which is why the endpoint exists. See
                      CrSepaMandateCaptureComponent#handleCaptureWithoutDocument.
                    -->
                    <!--
                      withoutDocumentNoReason shares this branch: same action, same activation, but
                      the missing-document reason is neither shown nor required (signature date only).
                    -->
                    @if (isWithoutDocumentBranch()) {

                        <div class="flex flex-column gap-2">

                            <p-message severity="info" styleClass="w-full">
                                <span class="text-sm">
                                    @if (isMissingDocumentReasonRequired()) {
                                        Ohne Beleg ist die Begründung Pflicht — sie tritt an die Stelle
                                        einer eigenen Berechtigung.
                                    } @else {
                                        Das Mandat wird ohne Beleg und ohne Begründung erfasst — nur das
                                        Unterschriftsdatum ist anzugeben.
                                    }
                                </span>
                            </p-message>

                            @if (bankAccountId) {
                                <!-- separate @if branches so the form is rebuilt when the capture type changes -->
                                @if (isMissingDocumentReasonRequired()) {
                                    <!-- AI: CRUD Object Component for the cr.CustomerBankAccount mandate fields, INPUT ONLY -->
                                    <mvs-crud-object
                                            [objectType]="'cr.CustomerBankAccount'"
                                            [objectId]="bankAccountId"
                                            [formControlOverwrite]="formControlOverwrite"
                                            [hideDefaultActions]="true"
                                            (onFormStateChange)="handleFormStateChange($event)"
                                            (onFormChange)="handleFormChange($event)">
                                    </mvs-crud-object>
                                } @else {
                                    <!-- AI: CRUD Object Component for the cr.CustomerBankAccount signature date only, INPUT ONLY -->
                                    <mvs-crud-object
                                            [objectType]="'cr.CustomerBankAccount'"
                                            [objectId]="bankAccountId"
                                            [formControlOverwrite]="formControlOverwriteNoReason"
                                            [hideDefaultActions]="true"
                                            (onFormStateChange)="handleFormStateChange($event)"
                                            (onFormChange)="handleFormChange($event)">
                                    </mvs-crud-object>
                                }

                                <div class="flex justify-content-end">
                                    <p-button
                                            [label]="getCaptureLabel()"
                                            icon="fa-regular fa-check"
                                            severity="success"
                                            [disabled]="!isWithoutDocumentSubmittable() || busy"
                                            [loading]="busy"
                                            (onClick)="handleCaptureWithoutDocument()">
                                    </p-button>
                                </div>
                            }
                        </div>
                    }

                    <!-- 2b. withDocument: upload of the signed mandate, then attach it -->
                    @if (getBranchInternalType() === CustomerSepaMandateSourceInternalTypeEnum.withDocument) {

                        <div class="flex flex-column gap-3">

                            <div class="flex flex-column gap-2">
                                <label for="crSepaMandateDocumentDate" class="font-medium">
                                    Unterschriftsdatum
                                </label>
                                <p-datePicker
                                        inputId="crSepaMandateDocumentDate"
                                        [(ngModel)]="documentSignatureDate"
                                        [showIcon]="true"
                                        [disabled]="busy"
                                        dateFormat="dd.mm.yy"
                                        placeholder="Datum wählen"
                                        appendTo="body"
                                        styleClass="w-full">
                                </p-datePicker>
                            </div>

                            @if (!selectedSource.documentTypeDtoId) {
                                <p-message severity="warn" styleClass="w-full">
                                    <span class="text-sm">
                                        An der Mandatsquelle ist kein Dokumenttyp hinterlegt. Ohne
                                        Dokumenttyp kann das Mandat nicht abgelegt werden.
                                    </span>
                                </p-message>
                            } @else if (!isDocumentUploadEnabled()) {
                                <p-message severity="info" styleClass="w-full">
                                    <span class="text-sm">
                                        Bitte zuerst das Unterschriftsdatum erfassen, danach kann das
                                        unterschriebene Mandat hochgeladen werden.
                                    </span>
                                </p-message>
                            } @else {
                                <mvs-dm-upload
                                        [objectIdentifier]="bankAccountIdentifier"
                                        [documentTypeId]="selectedSource.documentTypeDtoId"
                                        [multiple]="false"
                                        [showDocumentTypeDropdown]="false"
                                        [isDocumentTypeSelectionMode]="false"
                                        (onDocumentUploaded)="handleDocumentUploaded($event)">
                                </mvs-dm-upload>
                            }
                        </div>
                    }

                    <!-- 2c. withSmProcess: generate the mandate and hand it to the signing process -->
                    @if (getBranchInternalType() === CustomerSepaMandateSourceInternalTypeEnum.withSmProcess) {

                        <div class="flex flex-column gap-3">

                            <div class="flex justify-content-start">
                                <p-button
                                        label="Mandat erzeugen und zur Unterschrift geben"
                                        icon="fa-regular fa-signature"
                                        severity="primary"
                                        [disabled]="!isSignatureStartAllowed()"
                                        [loading]="busy"
                                        (onClick)="handleStartSignature()">
                                </p-button>
                            </div>

                            @if (state?.signingProcessId) {
                                <div class="flex flex-column gap-2 surface-50 border-round p-3">

                                    <div class="flex gap-2 flex-wrap">
                                        <span class="text-sm text-color-secondary w-12rem">Prozess-Id</span>
                                        <span class="text-sm font-medium">{{ state.signingProcessId }}</span>
                                    </div>

                                    @if (state.signerRoles?.length) {
                                        <span class="text-sm text-color-secondary">Unterzeichner</span>
                                        @for (signerRole of state.signerRoles; track signerRole.roleKey) {
                                            <div class="flex align-items-center gap-2 flex-wrap">
                                                <span class="text-sm w-12rem">{{ signerRole.roleKey }}</span>
                                                <span class="text-sm">{{ signerRole.displayName }}</span>
                                                <p-tag
                                                        severity="secondary"
                                                        [value]="getSignerStatusLabel(signerRole)">
                                                </p-tag>
                                            </div>
                                        }
                                    } @else {
                                        <span class="text-sm text-color-secondary">
                                            Es sind noch keine Unterzeichner zugeordnet.
                                        </span>
                                    }
                                </div>
                            }
                        </div>
                    }
                }
            </div>
        }
    </div>
}
